// Mic -> 16-bit PCM WAV blob. MediaRecorder only yields webm/ogg, which Twilio <Play> can't use.
// Phone audio is 8 kHz, so 16 kHz keeps a 90s message near 3 MB instead of ~9 MB at 48 kHz.
const TARGET_RATE = 16000;

const downsample = (chunks, fromRate) => {
  const input = new Float32Array(chunks.reduce((n, c) => n + c.length, 0));
  chunks.reduce((offset, c) => (input.set(c, offset), offset + c.length), 0);
  if (fromRate <= TARGET_RATE) return [input];
  const ratio = fromRate / TARGET_RATE;
  const out = new Float32Array(Math.floor(input.length / ratio));
  for (let i = 0; i < out.length; i += 1) {
    const start = Math.floor(i * ratio);
    const end = Math.min(input.length, Math.floor((i + 1) * ratio));
    let sum = 0;
    for (let j = start; j < end; j += 1) sum += input[j];
    out[i] = sum / Math.max(1, end - start);
  }
  return [out];
};

export const encodeWav = (rawChunks, rawRate) => {
  const sampleRate = Math.min(rawRate, TARGET_RATE);
  const chunks = downsample(rawChunks, rawRate);
  const length = chunks.reduce((n, c) => n + c.length, 0);
  const buffer = new ArrayBuffer(44 + length * 2);
  const view = new DataView(buffer);
  const str = (offset, s) => [...s].forEach((ch, i) => view.setUint8(offset + i, ch.charCodeAt(0)));
  str(0, "RIFF");
  view.setUint32(4, 36 + length * 2, true);
  str(8, "WAVE");
  str(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  str(36, "data");
  view.setUint32(40, length * 2, true);
  let offset = 44;
  chunks.forEach(c => {
    for (let i = 0; i < c.length; i += 1, offset += 2) {
      const s = Math.max(-1, Math.min(1, c[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
  });
  return new Blob([view], { type: "audio/wav" });
};

export const startWavRecording = async () => {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  const source = ctx.createMediaStreamSource(stream);
  const processor = ctx.createScriptProcessor(4096, 1, 1);
  const chunks = [];
  processor.onaudioprocess = e => chunks.push(new Float32Array(e.inputBuffer.getChannelData(0)));
  source.connect(processor);
  processor.connect(ctx.destination);
  return {
    stop: () => {
      processor.disconnect();
      source.disconnect();
      stream.getTracks().forEach(t => t.stop());
      const blob = encodeWav(chunks, ctx.sampleRate);
      ctx.close();
      return blob;
    }
  };
};
